import type { UseEndpointsResult } from '@/sections/asset/hooks/drawer/useEndpoints';
import { isWebApplication } from '@/sections/asset/utils/assetTypeDetection';
import type { Asset } from '@/types';

/**
 * True when a web application's OpenAPI spec resolved successfully but produced
 * zero endpoints — the signal to gray out the "REST API Endpoints" sub-tab.
 *
 * Kept deliberately narrow so the sub-tab stays reachable in the cases that
 * still have something to show:
 * - a parse error / oversized spec (`error` set) keeps the tab enabled so the
 *   error message and the spec download remain accessible;
 * - a still-loading spec (`status !== 'success'`) is never grayed out;
 * - non-web-application assets never expose the sub-tab at all.
 *
 * Extracted from the AttributesTab render so it can be unit-tested without
 * mounting the tab's heavy component graph.
 */
export function hasNoRestApiEndpoints(
  asset: Asset,
  endpoints: UseEndpointsResult['endpoints'],
  status: UseEndpointsResult['status'],
  error: UseEndpointsResult['error']
): boolean {
  return (
    isWebApplication(asset) &&
    status === 'success' &&
    !error &&
    endpoints.length === 0
  );
}
